import { createRouter, createWebHistory } from "vue-router";

import { useAuth } from "@/stores/auth";
import { addBreadcrumb, captureException, isSentryAvailable } from "@/modules/sentry";
import aidRoutes from "./modules/aid.js";
import bankRoutes from "./modules/bank.js";
import organizationRoutes from "./modules/organization.js";
import participantsRoutes from "./modules/participants.js";
import groupsRoutes from "./modules/groups.js";
import settingsRoutes from "./modules/settings.js";
import paymentsRoutes from "./modules/payments.js";
import replenishmentRoutes from "./modules/replenishment.js";
import authRoutes from "./modules/auth.js";
import landingRoutes from "./modules/landing.js";

import MainLayout from "@/layout/main/MainLayout.vue";

const routes: any[] = [
  {
    path: "/backoffice",
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: "/",
        name: "BackOfficeHome",
        meta: { requiresAuth: true, breadcrumb: ["BackOffice"] },
        component: () =>
          import(
            /* webpackChunkName: "backoffice-home" */ "@/views/backoffice/BackOfficeHome.vue"
          ),
      },
      ...aidRoutes,
      ...bankRoutes,
      ...organizationRoutes,
      ...participantsRoutes,
      ...groupsRoutes,
      ...settingsRoutes,
      ...paymentsRoutes,
      ...replenishmentRoutes,
      {
        path: ":pathMatch(.*)*",
        name: "BackOfficeNotFound",
        meta: { requiresAuth: true, breadcrumb: ["BackOffice", "404"] },
        component: () =>
          import(/* webpackChunkName: "system" */ "@/views/system/404View.vue"),
      },
    ],
  },
  ...authRoutes,
  ...landingRoutes,
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
});

router.beforeEach((to, from, next) => {
  if (isSentryAvailable()) {
    addBreadcrumb({
      category: "navigation",
      message: `Navigation: ${from.path} → ${to.path}`,
      level: "info",
      data: {
        from: from.path,
        to: to.path,
        toName: to.name != null ? String(to.name) : undefined,
        requiresAuth: to.meta.requiresAuth,
      },
    });
  }

  next();
});

router.onError((error) => {
  if (isSentryAvailable()) {
    captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: {
        errorType: "router-error",
      },
      extra: {
        currentRoute: router.currentRoute.value.path,
      },
    });
  }
});

router.beforeEach(async (to, _from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const authStore = useAuth();

  const isAuthenticated = authStore.isAuthenticated;

  if (requiresAuth && !isAuthenticated) {
    const restored = await authStore.checkAuth();

    if (restored) {
      next();

      return;
    }

    next("/auth/login");
  } else if (to.path === "/auth/login" && isAuthenticated) {
    next("/");
  } else {
    next();
  }
});

export default router;
